import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  inject,
  input,
  linkedSignal,
  output
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  DateService,
  isDefined,
  IsoDate, IsoDateTime,
  IsoDateTimeTz,
  IsoTime,
  IsoTimeTz,
  TimeService, TimeZoneService, toIsoDateTimeTz,
  toLocaleIsoDateTime
} from '@softline/core';
import { DateInputComponent } from '../date-input/date-input.component';
import { TimeInputComponent } from '../time-input/time-input.component';
import { UiCorePipesModule } from '../../../pipes/ui-core-pipes.module';


@Component({
    selector: 'soft-date-time-input',
    templateUrl: './date-time-input.component.html',
    styleUrls: ['./date-time-input.component.css'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DateTimeInputComponent),
            multi: true,
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DateInputComponent, TimeInputComponent, UiCorePipesModule]
})
export class DateTimeInputComponent implements ControlValueAccessor {

  private dateService = inject(DateService);
  private timeService = inject(TimeService);
  private timeZoneService = inject(TimeZoneService);

  private onChange: Function = () => {};
  onTouch: Function = () => {};

  readonly = input<boolean>(false);
  valueInput = input<IsoDateTimeTz | null>(null, {alias: 'value'});
  value = linkedSignal<IsoDateTimeTz | null>(() => {
    return this.valueInput() ?? null;
  })
  localeValue = computed(() => {
    const value = this.value();
    if(!isDefined(value)) return null;
    return toLocaleIsoDateTime(value);
  });
  valueChange = output<IsoDateTimeTz | null>();

  date = computed(() => {
    const value = this.localeValue();
    if(!isDefined(value)) return null;
    return value.split('T')[0] as IsoDate;
  })

  time = computed(() => {
    const value = this.localeValue();
    if(!isDefined(value)) return null;
    return value.split('T')[1] as IsoTime;
  })

  constructor() {}

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  writeValue(obj: any): void {
    this.value.set(obj);
  }

  setDate(value: IsoDate | null): void {
    if(!value)
      return;
    let time = this.time();
    if(!time)
      time = this.timeService.now();
    const dateTime = toIsoDateTimeTz(`${value}T${time}` as IsoDateTime, this.timeZoneService.timeZone());
    this.value.set(dateTime);
    this.onChange(dateTime);
    this.valueChange.emit(dateTime);
  }

  setTime(value: IsoTime | null): void {
    if(!value)
      return;
    let date = this.date();
    if(!date)
      date = this.dateService.now();
    const dateTime = toIsoDateTimeTz(`${date}T${value}` as IsoDateTime, this.timeZoneService.timeZone());
    this.value.set(dateTime);
    this.onChange(dateTime);
    this.valueChange.emit(dateTime);
  }
}
